/**
 * Owner: Masa Bayu
 * File: publication-preview-boundary.ts
 * Date:Time: 2026-09-15 20:19
 * Description: T1 preview/sample access boundary — preview ≠ full read entitlement.
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { assertValidId } from './guards';
export const QXREAD_PREVIEW_SCOPES = ['none', 'sample', 'chapter', 'percent'];
export function createQxreadPreviewBoundary(options) {
    assertNoForbiddenFoundationKey(options, 'QXRead preview boundary');
    assertValidId(options.publicationId, 'QXRead publicationId');
    const scope = options.scope ?? 'sample';
    if (!QXREAD_PREVIEW_SCOPES.includes(scope)) {
        throw new Error(`QXRead preview scope must be one of: ${QXREAD_PREVIEW_SCOPES.join(', ')}`);
    }
    return Object.freeze({
        publicationId: options.publicationId,
        scope,
        enabled: options.enabled ?? true,
    });
}
/**
 * Catalog/live listing never grants full read. Preview may be offered without entitlement.
 * Full content always requires T3 entitlement + T5 Reader launch.
 */
export function evaluatePublicationAccess(input) {
    if (input.ref.state !== 'bound') {
        return Object.freeze({
            allowed: false,
            mode: 'deny',
            reason: 'Publication must be bound to a QXP reference',
        });
    }
    if (input.availability === 'unavailable' || input.availability === 'coming_soon') {
        return Object.freeze({
            allowed: false,
            mode: 'deny',
            reason: `Publication availability "${input.availability}" blocks access`,
        });
    }
    if (input.entitlementActive) {
        return Object.freeze({
            allowed: true,
            mode: 'full_requires_entitlement',
            reason: 'Entitlement present — full read still executes in QXReader (T5), not QXRead',
        });
    }
    if (input.preview.enabled && input.preview.scope !== 'none') {
        return Object.freeze({
            allowed: true,
            mode: 'preview',
            reason: 'Preview/sample permitted without entitlement — not full content access',
        });
    }
    return Object.freeze({
        allowed: false,
        mode: 'deny',
        reason: 'Full content requires entitlement; preview disabled or unavailable',
    });
}
//# sourceMappingURL=publication-preview-boundary.js.map